<template>
    <div class="search-keywords">
        <StackRouterHeaderBar />
        <main>
            <h1 class="page-title" v-html="`# ${category.title}`" />

            <div class="keyword-desc" v-html="keywordDesc" />
            <div class="input-wrapper" :class="{ 'on-search': shouldShowAutoComplete }">
                <i class="material-icons search-icon">search</i>
                <input
                    placeholder="검색어를 입력하세요"
                    ref="query"
                    :value="query"
                    @keyup="onKeyUp"
                    @focus="showAutoComplete = true"
                    @blur="showAutoComplete = false"
                />
                <i v-show="query.length > 0" class="material-icons close-icon" @click="query = ''">cancel</i>

                <transition name="slide-down">
                    <div v-if="shouldShowAutoComplete" class="auto-complete">
                        <div class="items" @click="insertKeyword(query)">
                            # ‘{{ query }}’ <span class="direct">- 직접 입력</span>
                        </div>
                        <div
                            class="items"
                            :class="isSelected(keyword) ? 'selected' : ''"
                            v-for="keyword in lists"
                            :key="keyword.id"
                            @click="insertKeyword(keyword)"
                        >
                            # {{ keyword.name }}
                        </div>
                    </div>
                </transition>
            </div>
            <div class="keywords">
                <div class="keyword" v-for="keyword in keywords" :key="keyword.id" @click="deleteKeyword(keyword)">
                    {{ keyword.name }}
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import commonService from '@/services/common'
import debounce from '@/modules/debounce'

export default {
    name: 'SearchKeywordsPage',
    props: {
        category: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        query: '',
        lists: [],
        keywords: [],
        showAutoComplete: false,
    }),
    computed: {
        keywordDesc() {
            return this.category.description
        },
        shouldShowAutoComplete() {
            return this.query.length > 0 && this.showAutoComplete
        },
    },
    mounted() {
        this.$refs.query.focus()
        this.keywords = this.category.keywords
    },
    methods: {
        onKeyUp: debounce(function (e) {
            this.query = e.target.value
            this.search()
        }, 400),
        async search() {
            const data = await commonService.keywords(this.category.id, this.query)
            this.lists = [...data]
        },
        async insertKeyword(keyword) {
            if (!keyword) return

            if (this.keywords.some(e => e.name === keyword.name || e.name === keyword)) {
                this.$toast.error('KEYWORD_DUPLICATE_ERROR')
                this.query = ''
                return
            }

            if (typeof keyword === 'string') {
                try {
                    const res = await commonService.userKeywords.create({
                        name: keyword,
                        keywordCategoryId: this.category.id,
                    })
                    this.keywords.push(res.keyword)
                } catch (e) {
                    this.$toast.error(e)
                }
            } else {
                try {
                    await commonService.userKeywords.create({
                        keywordId: keyword.id,
                        keywordCategoryId: this.category.id,
                    })
                    this.keywords.push(keyword)
                } catch (e) {
                    this.$toast.error(e)
                }
            }

            this.query = ''
            this.$store.dispatch('loadMe')
        },
        async deleteKeyword(keyword) {
            if (!keyword) return

            try {
                await commonService.userKeywords.delete(keyword.id)
                const idx = this.keywords.findIndex(k => k.id === keyword.id)
                this.keywords.splice(idx, 1)
            } catch (e) {}

            this.$store.dispatch('loadMe')
        },
        isSelected(keyword) {
            return this.keywords.findIndex(k => k.id === keyword.id || k === keyword.name) > -1
        },
    },
}
</script>

<style lang="scss" scoped>
$input-height: 48px;
$input-width: calc(100vw - 32px);

.search-keywords {
    main {
        padding: 0 16px;
    }

    .page-title {
        margin-bottom: 40px;
        font-size: 24px;
        color: black;
        font-weight: 500;

        @include f-medium;
    }

    .keyword-desc {
        margin-bottom: 12px;
        font-size: 12px;
        color: $grey-06;

        @include f-medium;
    }

    .input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        height: $input-height;
        width: $input-width;
        padding: 12px 16px;

        &.on-search {
            border-radius: 12px 12px 0 0;
        }

        .material-icons {
            &.search-icon {
                color: $grey-08;
                font-size: 24px;
                margin-right: 12px;
            }

            &.close-icon {
                color: $grey-06;
                font-size: 16px;
            }
        }

        .auto-complete {
            position: absolute;
            top: calc(#{$input-height} - 4px);
            left: -1px;
            width: $input-width;
            max-height: 480px;
            padding: 16px 20px;
            border-radius: 0 0 12px 12px;
            border: solid 1px $grey-02;
            // border-top: 0;
            background-color: white;
            overflow-y: auto;

            .items {
                font-size: 16px;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: -0.2px;
                color: $grey-09;

                &:not(:last-child) {
                    margin-bottom: 16px;
                }

                &.selected {
                    color: $purple-primary;
                }

                span {
                    color: $grey-05;
                }
            }
        }
    }

    .keywords {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        margin-top: 32px;

        .keyword {
            margin-right: 8px;
            margin-bottom: 8px;
            padding: 0 12px;
            border-radius: 8px;
            height: 32px;
            font-size: 14px;
            border: solid 1px $purple-primary;
            color: $purple-primary;

            @include f-medium;
            @include center;
        }
    }
}
</style>
